import { useState } from "react";
import { MessageCircle, Mail, Link as LinkIcon, Copy, Check } from "lucide-react";
import WhatsAppIcon from "@/components/WhatsAppIcon";
import { toast } from "sonner";
import { api } from "@/lib/api";

/**
 * Repost menu — outbound sharing to social platforms.
 * Separate from "Share" which just copies the link / sends to contacts.
 */
export default function RepostMenu({ project, url, onClose }) {
  const [copiedCaption, setCopiedCaption] = useState(false);
  const fullUrl = url || (typeof window !== "undefined" ? window.location.href : "");
  const caption = `${project?.title || ""} — ${project?.location || ""}\n\n${(project?.description || "").slice(0, 240)}\n\n${fullUrl}`;

  const track = (channel) => {
    if (project?.id) api.post(`/projects/${project.id}/share?channel=${channel}`).catch(() => {});
  };

  const download = async () => {
    try {
      const src = project.cover_image || project.media?.[0]?.url;
      if (!src) return;
      const a = document.createElement("a");
      a.href = src.startsWith("http") ? src : `${process.env.REACT_APP_BACKEND_URL}${src}`;
      a.download = `${project.slug || "project"}.jpg`;
      a.target = "_blank";
      a.rel = "noreferrer";
      document.body.appendChild(a);
      a.click();
      a.remove();
      track("download");
      toast.success("Image opened — long-press to save");
    } catch {
      toast.error("Download failed");
    }
  };

  const copyCaption = async () => {
    try {
      await navigator.clipboard.writeText(caption);
      setCopiedCaption(true);
      track("copy_caption");
      setTimeout(() => setCopiedCaption(false), 2000);
      toast.success("Caption copied");
    } catch {
      toast.error("Copy failed");
    }
  };

  const items = [
    {
      key: "instagram",
      label: "Open Instagram",
      sub: "Paste caption & upload image",
      color: "bg-gradient-to-br from-[#F58529] via-[#DD2A7B] to-[#8134AF] text-white",
      icon: (
        <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current" aria-hidden="true">
          <path d="M12 2.2c3.2 0 3.6 0 4.8.1 1.2 0 1.8.2 2.2.4.6.2 1 .5 1.4.9.4.4.7.8.9 1.4.2.4.3 1 .4 2.2C22 8.4 22 8.8 22 12s0 3.6-.1 4.8c0 1.2-.2 1.8-.4 2.2-.2.6-.5 1-.9 1.4-.4.4-.8.7-1.4.9-.4.2-1 .3-2.2.4-1.2.1-1.6.1-4.8.1s-3.6 0-4.8-.1c-1.2 0-1.8-.2-2.2-.4-.6-.2-1-.5-1.4-.9-.4-.4-.7-.8-.9-1.4-.2-.4-.3-1-.4-2.2C2 15.6 2 15.2 2 12s0-3.6.1-4.8c0-1.2.2-1.8.4-2.2.2-.6.5-1 .9-1.4.4-.4.8-.7 1.4-.9.4-.2 1-.3 2.2-.4C8.4 2.2 8.8 2.2 12 2.2zm0 1.8c-3.1 0-3.5 0-4.7.1-.9 0-1.4.2-1.7.3-.4.2-.7.4-1 .7-.3.3-.5.6-.7 1-.1.3-.3.8-.3 1.7C3.2 8.5 3.2 8.9 3.2 12s0 3.5.1 4.7c0 .9.2 1.4.3 1.7.2.4.4.7.7 1 .3.3.6.5 1 .7.3.1.8.3 1.7.3 1.2.1 1.6.1 4.7.1s3.5 0 4.7-.1c.9 0 1.4-.2 1.7-.3.4-.2.7-.4 1-.7.3-.3.5-.6.7-1 .1-.3.3-.8.3-1.7.1-1.2.1-1.6.1-4.7s0-3.5-.1-4.7c0-.9-.2-1.4-.3-1.7-.2-.4-.4-.7-.7-1-.3-.3-.6-.5-1-.7-.3-.1-.8-.3-1.7-.3-1.2-.1-1.6-.1-4.7-.1zm0 3.3a4.7 4.7 0 1 1 0 9.4 4.7 4.7 0 0 1 0-9.4zm0 1.8a2.9 2.9 0 1 0 0 5.8 2.9 2.9 0 0 0 0-5.8zm4.9-2.2a1.1 1.1 0 1 1 0 2.2 1.1 1.1 0 0 1 0-2.2z"/>
        </svg>
      ),
      onClick: () => { track("instagram"); window.open("https://www.instagram.com/", "_blank", "noreferrer"); },
    },
    {
      key: "tiktok",
      label: "Open TikTok",
      sub: "Paste caption & upload video",
      color: "bg-black text-white",
      icon: (
        <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current" aria-hidden="true">
          <path d="M19.5 6.7a5.4 5.4 0 0 1-3.5-1.3 5.5 5.5 0 0 1-1.6-3H11v12a2.8 2.8 0 1 1-2-2.7V8.6a5.8 5.8 0 0 0-1-.1A5.8 5.8 0 1 0 13.8 14V9.3a8.1 8.1 0 0 0 5.7 2.1V7.8a5.3 5.3 0 0 1 0-1.1z"/>
        </svg>
      ),
      onClick: () => { track("tiktok"); window.open("https://www.tiktok.com/upload", "_blank", "noreferrer"); },
    },
    {
      key: "facebook",
      label: "Share to Facebook",
      sub: "Opens sharer with link",
      color: "bg-[#1877F2] text-white",
      icon: (
        <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current" aria-hidden="true">
          <path d="M22 12a10 10 0 1 0-11.6 9.9v-7H8v-2.9h2.4V9.8c0-2.4 1.4-3.7 3.6-3.7 1 0 2.1.2 2.1.2v2.3h-1.2c-1.2 0-1.5.7-1.5 1.5v1.8h2.6l-.4 2.9h-2.2v7A10 10 0 0 0 22 12z"/>
        </svg>
      ),
      onClick: () => { track("facebook_repost"); window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(fullUrl)}`, "_blank", "noreferrer"); },
    },
    {
      key: "pinterest",
      label: "Pin to Pinterest",
      sub: "Opens Pinterest picker",
      color: "bg-[#E60023] text-white",
      icon: (
        <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current" aria-hidden="true">
          <path d="M12 2a10 10 0 0 0-3.6 19.3c-.1-.8-.2-2 0-2.9l1.2-4.9s-.3-.6-.3-1.5c0-1.4.8-2.5 1.9-2.5.9 0 1.3.7 1.3 1.5 0 .9-.6 2.3-.9 3.6-.2 1.1.5 1.9 1.6 1.9 1.9 0 3.3-2 3.3-4.8 0-2.5-1.8-4.3-4.4-4.3-3 0-4.7 2.2-4.7 4.5 0 .9.3 1.8.8 2.4.1.1.1.2 0 .3l-.3 1.1c0 .2-.2.2-.3.1-1.2-.6-2-2.3-2-3.7 0-3 2.2-5.8 6.4-5.8 3.3 0 6 2.4 6 5.6 0 3.3-2.1 6-5 6-1 0-1.9-.5-2.2-1.1l-.6 2.3c-.2.8-.8 1.9-1.2 2.6A10 10 0 1 0 12 2z"/>
        </svg>
      ),
      onClick: () => {
        track("pinterest_repost");
        const img = project.cover_image?.startsWith("http") ? project.cover_image : `${process.env.REACT_APP_BACKEND_URL}${project.cover_image || ""}`;
        window.open(`https://pinterest.com/pin/create/button/?url=${encodeURIComponent(fullUrl)}&media=${encodeURIComponent(img)}&description=${encodeURIComponent(project.title || "")}`, "_blank", "noreferrer");
      },
    },
  ];

  return (
    <div className="fixed inset-0 z-[90] bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-6" onClick={onClose} data-testid="repost-menu">
      <div
        className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-xl p-5 sm:p-6 space-y-3"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-1">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-slate-500 mb-1">Repost</div>
            <h3 className="font-heading text-xl font-medium text-slate-950">Share outward</h3>
          </div>
          <button onClick={onClose} aria-label="Close" className="p-2 -mr-2 text-slate-400 hover:text-slate-700">✕</button>
        </div>
        <div className="grid grid-cols-2 gap-2" data-testid="repost-platforms">
          {items.map((item) => (
            <button
              key={item.key}
              onClick={item.onClick}
              data-testid={`repost-${item.key}`}
              className={`flex items-center gap-2 p-3 rounded-md ${item.color} transition-opacity hover:opacity-90 text-left`}
            >
              {item.icon}
              <div className="flex-1 min-w-0">
                <div className="text-xs font-medium">{item.label}</div>
                <div className="text-[10px] opacity-80">{item.sub}</div>
              </div>
            </button>
          ))}
        </div>
        <div className="border-t border-slate-100 pt-3 space-y-2">
          <button
            onClick={copyCaption}
            data-testid="repost-copy-caption"
            className="w-full flex items-center gap-3 p-3 border border-slate-200 hover:bg-slate-50 rounded-md text-sm text-slate-700"
          >
            {copiedCaption ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
            {copiedCaption ? "Caption copied" : "Copy caption with link"}
          </button>
          <button
            onClick={download}
            data-testid="repost-download"
            className="w-full flex items-center gap-3 p-3 border border-slate-200 hover:bg-slate-50 rounded-md text-sm text-slate-700"
          >
            <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current"><path d="M12 3v12l-4-4-1.4 1.4L12 18l5.4-5.4L16 11l-4 4V3h0zM4 19v2h16v-2H4z"/></svg>
            Download image
          </button>
          <a
            href={`https://wa.me/?text=${encodeURIComponent(caption)}`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => {
              track("whatsapp_repost");
              const href = e.currentTarget.getAttribute("href");
              e.preventDefault();
              const opened = window.open(href, "_blank", "noopener,noreferrer");
              if (!opened) {
                try { (window.top || window).location.href = href; }
                catch { window.location.href = href; }
              }
            }}
            data-testid="repost-whatsapp"
            className="w-full flex items-center gap-3 p-3 bg-[#25D366] hover:bg-[#1FB855] text-white rounded-md text-sm font-medium"
          >
            <WhatsAppIcon className="w-4 h-4" color="#FFFFFF" />
            Share caption via WhatsApp
          </a>
        </div>
      </div>
    </div>
  );
}
